import { Component, OnInit, Output, EventEmitter, OnDestroy, Input } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, FormControl, ValidatorFn, ValidationErrors } from '@angular/forms'
import { ContactModel } from '@models/contact.model';
import { AddContactMethod } from '@enums/addContactMethod.enum';
import { MessageService } from 'primeng/api';
import { SearchCountryField, CountryISO } from '@capgo/ngx-intl-tel-input';
import * as EmailValidator from 'email-validator';
import { CampaignsFacadeService } from '@services/campaigns/campaigns-facade.service';
import { LocationsFacadeService } from '@services/locations/locations-facade.service';
import { combineLatest, never, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Component({
  selector: 'app-contacs-form',
  templateUrl: './contacs-form.component.html',
  styleUrls: ['./contacs-form.component.sass']
})

export class ContacsFormComponent implements OnInit, OnDestroy {
  @Output() createContact = new EventEmitter<ContactModel>();
  @Output() editContact = new EventEmitter<ContactModel>();
  @Input() contact: ContactModel;
  @Input() create: boolean;
  contactForm: FormGroup;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.Canada, CountryISO.Australia, CountryISO.UnitedKingdom];
  campaigns$ = this.campaignsFacadeService.getLocationCampaignsBasicInfo$()
    .pipe(
      map(items => items.filter(item => !item.isDisabledMultiCampaign))
    )
  selectedLocation$ = this.locationFacadeService.getCurrentLocation$();
  _subs: Subscription = never().subscribe();
  selectedCampaign: string

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private campaignsFacadeService: CampaignsFacadeService,
    private locationFacadeService: LocationsFacadeService
  ) {}

  ngOnInit() {
    this.contactForm = this.fb.group({
      name: [this.contact.name, [Validators.required, this.ValidateName]],
      lastName: [this.contact.lastName],
      phoneNumber: this.contact.phoneNumber,
      email: [this.contact.email, [this.ValidateEmail]],
      campaignId: ["", this.validateCampaignId],
    }, {
      validator: (formControl) => {
        return ((((EmailValidator.validate(formControl.value.email)) || (formControl.value.phoneNumber && !formControl.controls['phoneNumber'].invalid))
          ? null : { invalidPhoneAndEmail: true }))
      }
    });
    this._subs.add(
      this.selectedLocation$
        .subscribe(location => {
          this.contactForm.patchValue({
            campaignId: location.preferredCampaign
          })
          this.selectedCampaign = location.preferredCampaign
        })
    );
    this._subs.add(
      combineLatest(
        [
          this.selectedLocation$,
          this.campaigns$
        ]
      ).pipe(
        filter(([location, campaigns]) => campaigns.length > 0),
        map(([location, campaigns]) => campaigns.filter(item => item.id == location.preferredCampaign)),
        map(items => items[0])
      ).subscribe(campaign => {
        this.selectedCampaign = campaign.id;
        this.contactForm.patchValue({
          campaignId: this.selectedCampaign
        })
      })
    )
    this.campaignsFacadeService.getLocationCampaignsBasicInfo()
  }

  changePreferredCountries() {
    this.preferredCountries = [CountryISO.India, CountryISO.Canada];
  }

  onSubmit(form: FormGroup) {
    this.contactForm.value.phoneNumber = this.contactForm.value.phoneNumber ? this.contactForm.value.phoneNumber.internationalNumber : ''
    const contact = {
      ...this.contact,
      ...this.contactForm.value,
      addMethod: AddContactMethod.quickSend
    }
    !this.create
      ? this.editContact.emit(contact)
      : this.createContact.emit(contact);
    this.contactForm.setValue({ name: '', lastName: '', phoneNumber: '', email: '', campaignId: "" })
    this.contactForm = this.fb.group({
      name: [this.contact.name, [Validators.required, this.ValidateName]],
      lastName: [this.contact.lastName],
      phoneNumber: this.contact.phoneNumber,
      email: [this.contact.email, [this.ValidateEmail]],
      campaignId: [this.selectedCampaign, this.validateCampaignId],
    }, {
      validator: (formControl) => {
        return ((((EmailValidator.validate(formControl.value.email)) || (formControl.value.phoneNumber && !formControl.controls['phoneNumber'].invalid))
          ? null : { invalidPhoneAndEmail: true }))
      }
    });
  }

  ngOnDestroy() {
    if (this._subs) this._subs.unsubscribe()
  }

  ValidateName(control: AbstractControl) {
    if (control.value && control.value.trim().length) {
      return null;
    }
    return { invalid: true };
  }

  ValidateEmail(control: AbstractControl) {
    if ((EmailValidator.validate(control.value) || control.value == '' || control.value == null)) {
      return null;
    }
    return { invalid: true };
  }

  oneOfControlRequired(...controls: AbstractControl[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      for (const aControl of controls) {
        if (!Validators.required(aControl)) {
          return null;
        }
      }
      return { oneOfRequired: true };
    };
  }
  changeContactCampaign(campaignId: String) {
    this.contactForm.patchValue({
      campaignId: campaignId
    })
  }
  validateCampaignId(control: AbstractControl) {
    if (control.value && control.value.length > 0) {
      return null;
    }
    return { invalid: true };
  }
}
